/**
 * Polling hook that tracks plugin publish pipeline status.
 * Polls GET /plugins/submissions/:id every 3s until a terminal state is reached.
 */

import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import api from '@/lib/api';
import { queryKeys } from '@/lib/query-keys';

export interface PublishStatus {
  submissionId: string | null;
  status: 'reviewing' | 'human_review' | 'building' | 'published' | 'rejected' | 'failed' | null;
  reviewStatus: string | null;
  reviewFindings: unknown[] | null;
  errorMessage: string | null;
  archId: string | null;
  isPolling: boolean;
}

// `human_review` is treated as terminal for polling: human action takes hours
// to days, so hammering the API is wasteful. The contributor is notified by
// email when state changes, and the dev-console submission detail page
// re-fetches on demand.
const TERMINAL_STATUSES = new Set(['human_review', 'published', 'rejected', 'failed']);
const POLL_INTERVAL_MS = 3_000;

const INITIAL_STATUS: PublishStatus = {
  submissionId: null,
  status: null,
  reviewStatus: null,
  reviewFindings: null,
  errorMessage: null,
  archId: null,
  isPolling: false,
};

export function usePublishStatus(submissionId: string | null): PublishStatus {
  const { data } = useQuery({
    queryKey: queryKeys.pluginBuilder.publishStatus(submissionId ?? '__none__'),
    queryFn: async () => {
      const result = await api.getPluginSubmission(submissionId!);

      const sub = result.submission;
      const review = result.review;

      const allFindings = [
        ...((review?.codeFindings as unknown[]) ?? []),
        ...((review?.dependencyFindings as unknown[]) ?? []),
        ...((review?.policyFindings as unknown[]) ?? []),
        ...((review?.qualityFindings as unknown[]) ?? []),
      ];

      return {
        submissionId: submissionId!,
        status: sub.status as PublishStatus['status'],
        reviewStatus: review?.status ?? null,
        reviewFindings: allFindings.length > 0 ? allFindings : null,
        errorMessage: sub.errorMessage,
        archId: sub.archId,
        isTerminal: TERMINAL_STATUSES.has(sub.status),
      };
    },
    enabled: !!submissionId,
    refetchInterval: (query) => {
      // Stop polling once we reach a terminal state
      if (query.state.data?.isTerminal) {
        return false;
      }
      return POLL_INTERVAL_MS;
    },
    // On error, keep polling — transient failures shouldn't stop tracking
    retry: true,
    retryDelay: POLL_INTERVAL_MS,
  });

  return useMemo<PublishStatus>(() => {
    if (!submissionId || !data) {
      return submissionId
        ? {
            ...INITIAL_STATUS,
            submissionId,
            status: 'reviewing',
            isPolling: true,
          }
        : INITIAL_STATUS;
    }

    return {
      submissionId: data.submissionId,
      status: data.status,
      reviewStatus: data.reviewStatus,
      reviewFindings: data.reviewFindings,
      errorMessage: data.errorMessage,
      archId: data.archId,
      isPolling: !data.isTerminal,
    };
  }, [submissionId, data]);
}
